import React, { Fragment, useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import MetaTags from "react-meta-tags";
import { BreadcrumbsItem } from "react-breadcrumbs-dynamic";
import LayoutOne from "../../layouts/LayoutOne";
import Breadcrumb from "../../wrappers/breadcrumb/Breadcrumb";
import { AppInstance } from "../../api/instance";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useSettings } from "../../hooks/useSettings";

const OrderDetail = ({ location, userInfo, currency }) => {
  const { pathname } = location;
  const { id } = useParams();
  const history = useHistory();
  const { siteName } = useSettings();
  const [orderDetail, setOrderDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReviewSuccessModal, setShowReviewSuccessModal] = useState(false);
  const [isClosingReviewSuccessModal, setIsClosingReviewSuccessModal] =
    useState(false);

  const getOrderDetail = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await AppInstance.get(`/order-item`, {
        params: { id },
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      setOrderDetail(data.data);
    } catch (error) {
      console.log(error);
      setError("Failed to load order details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userInfo && userInfo.token) {
      getOrderDetail();
    }
  }, [id, userInfo]);

  useEffect(() => {
    if (!location?.state?.showReviewSuccess) return;

    setShowReviewSuccessModal(true);
    setIsClosingReviewSuccessModal(false);

    const closeTimer = setTimeout(() => {
      setIsClosingReviewSuccessModal(true);
      setTimeout(() => {
        setShowReviewSuccessModal(false);
        setIsClosingReviewSuccessModal(false);
      }, 260);
    }, 2200);

    history.replace({
      pathname: location.pathname,
      search: location.search,
      state: {},
    });

    return () => clearTimeout(closeTimer);
  }, [history, location]);

  const latestOrderStatus = orderDetail?.statuses?.[0]?.status;
  const canReview = latestOrderStatus === "COMPLETED";

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatStatusText = (status) => String(status || "").replace(/_/g, " ");

  const getRatingBadgeClass = (rating) => {
    const parsed = parseFloat(rating);
    const normalized = Number.isFinite(parsed) ? Math.round(parsed) : 0;
    const clamped = Math.min(5, Math.max(1, normalized));
    return `rating-${clamped}`;
  };

  const getTimelineStatusClass = (status, index, totalStatuses) => {
    if (index === 0) {
      return "current";
    } else if (index < totalStatuses - 1) {
      return "completed";
    } else {
      return "pending";
    }
  };

  const getStatusIcon = (status) => {
    const statusIcons = {
      CREATED: "pe-7s-cart",
      PAYMENT_PROCESSING: "pe-7s-credit",
      PLACED: "pe-7s-check",
      CONFIRMED: "pe-7s-check",
      PROCESSING: "pe-7s-tools",
      SHIPPED: "pe-7s-plane",
      DELIVERED: "pe-7s-home",
      CANCELLED: "pe-7s-close",
      RETURNED: "pe-7s-refresh",
    };
    return statusIcons[status] || "pe-7s-info";
  };

  const getVariantColorHex = (attributeName, attributeValue) => {
    const name = String(attributeName || "").toLowerCase();
    const rawValue = String(attributeValue || "").trim();
    const normalizedValue = rawValue.startsWith("#")
      ? rawValue.slice(1)
      : rawValue;
    const isHexColor = /^[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(
      normalizedValue
    );

    if (!name.includes("color") || !isHexColor) return null;

    return `#${normalizedValue}`;
  };

  const closeReviewSuccessModal = () => {
    setIsClosingReviewSuccessModal(true);
    setTimeout(() => {
      setShowReviewSuccessModal(false);
      setIsClosingReviewSuccessModal(false);
    }, 260);
  };

  const formatRatingReviewDisplay = (ratingReview) => {
    const text = String(ratingReview?.review || "").trim();
    if (!text) {
      return { title: "", body: "" };
    }
    const lines = text.split(/\n/);
    if (lines.length > 1 && lines[0].trim()) {
      return { title: lines[0].trim(), body: lines.slice(1).join("\n").trim() };
    }
    return { title: "", body: text };
  };


  if (isLoading) {
    return (
      <Fragment>
        <MetaTags>
          <title>{siteName} | Order Detail</title>
          <meta name="description" content={`Order Detail page of ${siteName}`} />
        </MetaTags>
        <LayoutOne headerTop="visible" headerContainerClass>
          <div className="cart-main-area pt-90 pb-100">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-12">
                  <div className="text-center">
                    <div className="flone-preloader">
                      <span></span>
                      <span></span>
                    </div>
                    <p className="mt-3">Loading order details...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </LayoutOne>
      </Fragment>
    );
  }

  if (error || !orderDetail) {
    return (
      <Fragment>
        <MetaTags>
          <title>{siteName} | Order Detail</title>
          <meta name="description" content={`Order Detail page of ${siteName}`} />
        </MetaTags>
        <LayoutOne headerTop="visible" headerContainerClass>
          <div className="cart-main-area pt-90 pb-100">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-12">
                  <div className="text-center">
                    <div className="item-empty-area__icon mb-30">
                      <i
                        className="pe-7s-close-circle"
                        style={{ fontSize: "80px" }}
                      ></i>
                    </div>
                    <div className="item-empty-area__text">
                      {error || "Order not found"} <br />
                      <Link
                        to={process.env.PUBLIC_URL + "/my-orders"}
                        className="btn btn-primary mt-3"
                      >
                        Back to Orders
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </LayoutOne>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <MetaTags>
        <title>{siteName} | Order Detail - {orderDetail.code}</title>
        <meta
          name="description"
          content={`Order Detail for ${orderDetail.code} - ${siteName}`}
        />
      </MetaTags>

      <BreadcrumbsItem to={process.env.PUBLIC_URL + "/"}>Home</BreadcrumbsItem>
      <BreadcrumbsItem to={process.env.PUBLIC_URL + "/my-orders"}>
        My Orders
      </BreadcrumbsItem>
      <BreadcrumbsItem to={process.env.PUBLIC_URL + pathname}>
        Order Detail
      </BreadcrumbsItem>

      <LayoutOne headerTop="visible" headerContainerClass>
        <Breadcrumb />
        <div className="cart-main-area pt-90 pb-100">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="order-detail-wrapper">
                  {/* Order Header */}
                  <div className="order-header mb-4">
                    <div className="row align-items-center justify-content-between">
                      <div className="col-md-6">
                        <h3 className="mb-2" style={{ textAlign: "left" }}>
                          Order #{orderDetail.code}
                        </h3>
                        <p className="mb-0" style={{ textAlign: "left" }}>
                          Placed on {formatDate(orderDetail.created_at)}
                        </p>
                      </div>
                      <div className="col-md-6 text-md-end d-flex justify-content-end align-items-center gap-2">
                        {(!canReview || orderDetail.rating_review) && (
                          <span
                            className={`badge`}
                            style={{
                              marginRight: "10px",
                              padding: "15px 35px",
                              borderRadius: "0",
                              border: "1px solid black",
                              backgroundColor: "black",
                              color: "white",
                              fontSize: "11px",
                            }}
                          >
                            {formatStatusText(orderDetail.statuses[0]?.status)}
                          </span>
                        )}
                        {canReview && !orderDetail.rating_review && (
                          <Link
                            to={
                              process.env.PUBLIC_URL +
                              "/order-detail/" +
                              id +
                              "/review"
                            }
                            className="btn order-header-review-btn"
                          >
                            Add Review
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    {/* Product Details */}
                    <div className="col-lg-8">
                      <div className="order-product-details mb-4">
                        <h4 className="mb-3">Product Details</h4>
                        <div className="product-card border rounded p-3">
                          <div className="order-detail-product-layout">
                            <div className="order-detail-product-image">
                              <Link
                                to={`/product/${orderDetail.item.id}`}
                              >
                                <img
                                  className="img-fluid rounded"
                                  style={{
                                    width: "100px",
                                  }}
                                  src={
                                    orderDetail.item.images &&
                                    orderDetail.item.images.length > 0
                                      ? orderDetail.item.images[0].url
                                      : orderDetail.item.product.cover
                                      ? process.env.REACT_APP_IMAGE_BASE_URL +
                                        orderDetail.item.product.cover
                                      : ""
                                  }
                                  alt={orderDetail.item.name}
                                />
                              </Link>
                            </div>
                            <div className="order-detail-product-content">
                              <h5 className="pb-2 mb-0">
                                <Link
                                  to={`/product/${orderDetail.item.id}`}
                                  className="text-decoration-none"
                                >
                                  {`${orderDetail.item.product.name}${orderDetail.item.name ? ` - ${orderDetail.item.name}` : ""}`}
                                </Link>
                              </h5>

                              {/* Product Attributes */}
                              {orderDetail.item.attributes &&
                                orderDetail.item.attributes.length > 0 && (
                                  <div className="product-attributes mb-2">
                                    {orderDetail.item.attributes.map(
                                      (attr, index) => {
                                        const attributeName =
                                          attr?.attribute?.name;
                                        const colorHex = getVariantColorHex(
                                          attributeName,
                                          attr?.value
                                        );

                                        return (
                                        <span
                                          key={index}
                                          className="badge me-2"
                                          style={{
                                            fontWeight: 500,
                                          }}
                                        >
                                          {attributeName}:{" "}
                                          {colorHex ? (
                                            <span
                                              className="order-detail-color-swatch"
                                              style={{
                                                backgroundColor: colorHex,
                                              }}
                                              title={colorHex}
                                            ></span>
                                          ) : (
                                            attr.value
                                          )}
                                        </span>
                                        );
                                      }
                                    )}
                                  </div>
                                )}

                              <div className="row order-detail-product-pricing-row">
                                <div className="col-md-6 col-6">
                                  <small>Unit Price</small>
                                  <p className="mb-0">
                                    {currency.currencySymbol}{" "}
                                    {(orderDetail.unit_price * currency.currencyRate).toFixed(2)}
                                  </p>
                                </div>
                                <div className="col-md-6 col-6">
                                  <small>Quantity</small>
                                  <p className="mb-0">{orderDetail.quantity}</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {orderDetail.rating_review && (
                            <div className="order-item-feedback">
                              <div className="order-item-feedback-top">
                                <span className="order-item-feedback-heading">
                                  Your feedback
                                </span>
                                <span
                                  className={`order-item-feedback-rating-badge ${getRatingBadgeClass(
                                    orderDetail.rating_review.rating
                                  )}`}
                                >
                                  {(() => {
                                    const r = parseFloat(
                                      orderDetail.rating_review.rating
                                    );
                                    const n = Number.isFinite(r) ? r : 0;
                                    const label = Number.isInteger(n)
                                      ? String(n)
                                      : n.toFixed(1);
                                    return (
                                      <>
                                        {label}{" "}
                                        <i
                                          className="fa fa-star"
                                          aria-hidden="true"
                                        />
                                      </>
                                    );
                                  })()}
                                </span>
                              </div>
                              {(() => {
                                const { title, body } = formatRatingReviewDisplay(
                                  orderDetail.rating_review
                                );
                                return (
                                  <div className="order-item-feedback-copy">
                                    {title ? (
                                      <p className="order-item-feedback-title">
                                        {title}
                                      </p>
                                    ) : null}
                                    {body ? (
                                      <p className="order-item-feedback-body">
                                        {body}
                                      </p>
                                    ) : null}
                                  </div>
                                );
                              })()}
                              {canReview && (
                                <div className="order-item-feedback-actions">
                                  <Link
                                    to={
                                      process.env.PUBLIC_URL +
                                      "/order-detail/" +
                                      id +
                                      "/review"
                                    }
                                    className="order-item-feedback-edit"
                                  >
                                    Edit review
                                  </Link>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Order Status Timeline */}
                      <div className="order-status-timeline mb-4">
                        <h4 className="mb-3">Order Status</h4>
                        <div className="timeline-wrapper">
                          {orderDetail.statuses.map((status, index) => (
                            <div
                              key={status.id}
                              className={`timeline-item ${getTimelineStatusClass(
                                status.status,
                                index,
                                orderDetail.statuses.length
                              )}`}
                            >
                              <div className="timeline-icon">
                                <div className="rounded-circle">
                                  <i
                                    className={`${getStatusIcon(
                                      status.status
                                    )}`}
                                  ></i>
                                </div>
                              </div>
                              <div className="timeline-content">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <h6>{formatStatusText(status.status)}</h6>
                                    <p>{status.description}</p>
                                  </div>
                                  <small>{formatDate(status.created_at)}</small>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                    </div>

                    {/* Order Summary */}
                    <div className="col-lg-4">
                      <div className="order-summary">
                        <h4 className="mb-3">Order Summary</h4>
                        <div className="summary-card border rounded p-3">
                          <div className="summary-item d-flex justify-content-between mb-2">
                            <span>Order Number:</span>
                            <span className="fw-bold">
                              {orderDetail.order.order_number}
                            </span>
                          </div>
                          <div className="summary-item d-flex justify-content-between mb-2">
                            <span>Order Date:</span>
                            <span>{formatDate(orderDetail.created_at)}</span>
                          </div>
                          <div className="summary-item d-flex justify-content-between mb-2">
                            <span>Payment Status:</span>
                            <span
                              className={`badge ${orderDetail.order.payment_status === "PAID"
                                  ? "text-success"
                                  : "text-warning"
                                }`}
                            >
                              {orderDetail.order.payment_status}
                            </span>
                          </div>
                          {/* <hr /> */}
                          <div className="summary-item d-flex justify-content-between mb-2">
                            <span>Subtotal:</span>
                            <span>
                              {currency.currencySymbol}{" "}
                              {(orderDetail.total_price * currency.currencyRate).toFixed(2)}
                            </span>
                          </div>
                          <div className="summary-item d-flex justify-content-between mb-2">
                            <span>Tax:</span>
                            <span>
                              {currency.currencySymbol}{" "}
                              {(orderDetail.unit_tax * currency.currencyRate).toFixed(2)}
                            </span>
                          </div>
                          <div className="summary-item d-flex justify-content-between mb-2">
                            <span>Delivery Charge:</span>
                            <span>
                              {currency.currencySymbol}{" "}
                              {(orderDetail.order.delivery_charge * currency.currencyRate).toFixed(2)}
                            </span>
                          </div>
                          {parseFloat(orderDetail.order.offer_amount) > 0 && (
                            <div className="summary-item d-flex justify-content-between mb-2">
                              <span>Discount:</span>
                              <span>
                                -{currency.currencySymbol}{" "}
                                {(parseFloat(orderDetail.order.offer_amount) * currency.currencyRate).toFixed(2)}
                              </span>
                            </div>
                          )}
                          <hr />
                          <div className="summary-item d-flex justify-content-between mb-0">
                            <span>Total:</span>
                            <span>
                              {currency.currencySymbol}{" "}
                              {(orderDetail.order.total_amount * currency.currencyRate).toFixed(2)}
                            </span>
                          </div>
                        </div>

                        {/* Shipping Information */}
                        <div className="shipping-info mt-4">
                          <h4 className="mb-3">Shipping Information</h4>
                          <div className="shipping-card border rounded p-3">
                            <div className="address-info">
                              <h6 className="mb-2">Shipping Address</h6>
                              {(() => {
                                try {
                                  const addressData = JSON.parse(
                                    orderDetail.order.address
                                  );
                                  return (
                                    <div>
                                      <p className="mb-3">{addressData.name}</p>
                                      <p className="mb-2">
                                        {addressData.street_address}
                                      </p>
                                      <p className="mb-2">
                                        {addressData.city}, {addressData.state}{" "}
                                        {addressData.postal_code}
                                      </p>
                                      <p className="mb-1">
                                        {addressData.country}
                                      </p>
                                      <p className="mb-0">
                                        Phone: {addressData.phone_number}
                                      </p>
                                    </div>
                                  );
                                } catch (e) {
                                  return (
                                    <p>Address information not available</p>
                                  );
                                }
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="order-actions mt-4 text-center">
                    <Link
                      to={process.env.PUBLIC_URL + "/my-orders"}
                      className="btn btn-outline-primary me-2"
                    >
                      <i
                        className="pe-7s-back-2 me-1"
                        style={{
                          paddingRight: "10px",
                        }}
                      ></i>
                      Back to Orders
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {showReviewSuccessModal && (
          <div
            className={`review-success-modal-overlay ${
              isClosingReviewSuccessModal ? "is-closing" : ""
            }`}
            onClick={closeReviewSuccessModal}
          >
            <div
              className={`review-success-modal ${
                isClosingReviewSuccessModal ? "is-closing" : ""
              }`}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="review-success-modal-close"
                onClick={closeReviewSuccessModal}
                aria-label="Close success modal"
              >
                <i className="pe-7s-close" />
              </button>
              <div className="review-success-modal-icon">
                <i className="fa fa-check" aria-hidden="true" />
              </div>
              <h5>Thank you!</h5>
              <p className="review-success-modal-line mb-0">
                We really appreciate you taking a moment to share your
                experience.
              </p>
            </div>
          </div>
        )}
      </LayoutOne>
    </Fragment>
  );
};

OrderDetail.propTypes = {
  userInfo: PropTypes.object,
  currency: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    userInfo: state.userInfo,
    currency: state.currencyData,
  };
};

export default connect(mapStateToProps)(OrderDetail);
